import { Role, rolesManipulator } from 'auth';
import { useMemo } from 'react';
import { createSearchParams, useNavigate, useSearchParams } from 'react-router-dom';
import { REGISTER_ID_SEARCH_PARAM_KEY } from '@/modules/common/constants/search-params.ts';
import { useRegisterTypeStrings } from '@/modules/common/hooks/useRegisterTypeStrings.tsx';
import { useCurrentUserRegistersQuery } from '@/modules/investor-dashboard/queries/registers.tsx';
import { BetaBadge } from '@/modules/issuer-dashboard/pages/IssuerAIChat.tsx';
import { useIsAllNotificationsRead } from '@/modules/issuer-dashboard/queries/event-logs.ts';
import { issuerDashboardPaths } from '@/modules/issuer-dashboard/routes.tsx';
import { SidebarMenuItem } from 'ui';
import {
  AIChatGrayIcon,
  AIChatWhiteIcon,
  BlockchainGrayIcon,
  BlockchainWhiteIcon,
  BriefcaseGrayIcon,
  BriefcaseWhiteIcon,
  CertificateGrayIcon,
  CertificateWhiteIcon,
  DistributionGrayIcon,
  DistributionWhiteIcon,
  EventsGrayIcon,
  EventsWhiteIcon,
  FileGreyIcon,
  FileWhiteIcon,
  FilesIcon,
  FilesWhiteIcon,
  HistoryGrayIcon,
  HistoryWhiteIcon,
  HomeGrayIcon,
  HomeWhiteIcon,
  IssuerGrayIcon,
  IssuerWhiteIcon,
  ListGrayIcon,
  ListWhiteIcon,
  MeetingGrayIcon,
  MeetingWhiteIcon,
  MessageChatDisabledIcon,
  MessageChatIcon,
  NavHistoryGrayIcon,
  NavHistoryWhiteIcon,
  NotificationsDisabledIcon,
  NotificationsGrayIcon,
  NotificationsWhiteIcon,
  PaymentsGrayIcon,
  PaymentsWhiteIcon,
  PieChartGeyIcon,
  PieChartWhiteIcon,
  ReinvestmentsGrayIcon,
  ReinvestmentsWhiteIcon,
  SeedingDisabledIcon,
  SeedingIcon,
  SeedingWhiteIcon,
  TransactionsGrayIcon,
  TransactionsWhiteIcon,
  TransfersGrayIcon,
  TransfersWhiteIcon,
  UserManagementIcon,
  UserManagementInactiveIcon,
  UserManagementWhiteIcon,
  WhiteMessageChatIcon,
} from 'ui/icons';
import { RegisterType } from 'api';

export const useIssuerSidebarOptions = (register_id?: string | null): SidebarMenuItem[] => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const userRole = rolesManipulator.current_user_role;
  const getRegisterTypeStrings = useRegisterTypeStrings();
  const register = params.get(REGISTER_ID_SEARCH_PARAM_KEY) || register_id || '';
  const { data: isAllNotificationRead } = useIsAllNotificationsRead(!register);
  const { data: registers } = useCurrentUserRegistersQuery();

  const currentRegisterType = useMemo(
    () =>
      (registers?.data || []).find(({ register_id }) => register_id === +register)?.register_obj
        ?.type,
    [register, registers?.data],
  );

  const search = createSearchParams({
    register,
  }).toString();

  return [
    {
      label: 'Dashboard',
      icon: HomeGrayIcon,
      activeIcon: HomeWhiteIcon,
      disabledIcon: HomeGrayIcon,
      pathname: issuerDashboardPaths.static.dashboard,
      onClick: () =>
        navigate(
          {
            pathname: issuerDashboardPaths.static.dashboard,
            search,
          },
          {
            replace: false,
          },
        ),
    },
    {
      label: 'Securities',
      icon: PieChartGeyIcon,
      activeIcon: PieChartWhiteIcon,
      disabledIcon: PieChartGeyIcon,
      pathname: issuerDashboardPaths.static.securities,
      onClick: () =>
        navigate(
          {
            pathname: issuerDashboardPaths.static.securities,
            search,
          },
          {
            replace: false,
          },
        ),
      subItems: [
        {
          label: 'Classes',
          icon: ListGrayIcon,
          activeIcon: ListWhiteIcon,
          disabledIcon: ListGrayIcon,
          pathname: issuerDashboardPaths.static.classes,
          onClick: () =>
            navigate(
              {
                pathname: issuerDashboardPaths.static.classes,
                search,
              },
              {
                replace: false,
              },
            ),
        },
        {
          label: `${getRegisterTypeStrings('NAV')}`,
          icon: NavHistoryGrayIcon,
          activeIcon: NavHistoryWhiteIcon,
          disabledIcon: NavHistoryGrayIcon,
          pathname: issuerDashboardPaths.static.nav,
          onClick: () =>
            navigate(
              {
                pathname: issuerDashboardPaths.static.nav,
                search,
              },
              {
                replace: false,
              },
            ),
          // disabled: !checkAccessEnv(['dev', 'staging']),
          // disabledHint: 'We are working on it',
        },
        {
          label: 'Transactions',
          icon: TransactionsGrayIcon,
          activeIcon: TransactionsWhiteIcon,
          disabledIcon: TransactionsGrayIcon,
          pathname: issuerDashboardPaths.static.transactions,
          onClick: () =>
            navigate(
              {
                pathname: issuerDashboardPaths.static.transactions,
                search,
              },
              {
                replace: false,
              },
            ),
        },
        {
          label: 'Transfers',
          icon: TransfersGrayIcon,
          activeIcon: TransfersWhiteIcon,
          disabledIcon: TransfersGrayIcon,
          pathname: issuerDashboardPaths.static.transfers,
          onClick: () =>
            navigate(
              {
                pathname: issuerDashboardPaths.static.transfers,
                search,
              },
              {
                replace: false,
              },
            ),
        },
        {
          label: 'Buy backs',
          icon: BriefcaseGrayIcon,
          activeIcon: BriefcaseWhiteIcon,
          disabledIcon: BriefcaseGrayIcon,
          pathname: issuerDashboardPaths.static.buyBack,
          disabled: currentRegisterType !== RegisterType.Company,
          disabledHint: 'Available only for company',
          onClick: () =>
            navigate(
              {
                pathname: issuerDashboardPaths.static.buyBack,
                search,
              },
              {
                replace: false,
              },
            ),
        },
        {
          label: 'Certificates',
          icon: CertificateGrayIcon,
          activeIcon: CertificateWhiteIcon,
          disabledIcon: CertificateGrayIcon,
          pathname: issuerDashboardPaths.static.certificates,
          onClick: () =>
            navigate(
              {
                pathname: issuerDashboardPaths.static.certificates,
                search,
              },
              {
                replace: false,
              },
            ),
        },
        {
          label: 'Operations',
          icon: HistoryGrayIcon,
          activeIcon: HistoryWhiteIcon,
          disabledIcon: HistoryGrayIcon,
          pathname: issuerDashboardPaths.static.operations,
          onClick: () =>
            navigate(
              {
                pathname: issuerDashboardPaths.static.operations,
                search,
              },
              {
                replace: false,
              },
            ),
        },
      ].filter(
        item =>
          item.label !== 'Buy backs' ||
          (item.label === 'Buy backs' && currentRegisterType === RegisterType.Company),
      ),
    },
    {
      label: getRegisterTypeStrings('Dividend', { capitalize: true, plural: true }),
      icon: DistributionGrayIcon,
      activeIcon: DistributionWhiteIcon,
      disabledIcon: DistributionGrayIcon,
      pathname: issuerDashboardPaths.static.distributions,
      onClick: () =>
        navigate(
          {
            pathname: issuerDashboardPaths.static.distributions,
            search,
          },
          {
            replace: false,
          },
        ),
      subItems: [
        {
          label: 'Events',
          icon: EventsGrayIcon,
          activeIcon: EventsWhiteIcon,
          disabledIcon: EventsGrayIcon,
          pathname: issuerDashboardPaths.static.events,
          onClick: () =>
            navigate(
              {
                pathname: issuerDashboardPaths.static.events,
                search,
              },
              {
                replace: false,
              },
            ),
        },
        {
          label: 'Reinvestments',
          icon: ReinvestmentsGrayIcon,
          activeIcon: ReinvestmentsWhiteIcon,
          disabledIcon: ReinvestmentsGrayIcon,
          pathname: issuerDashboardPaths.static.reInvestments,
          onClick: () =>
            navigate(
              {
                pathname: issuerDashboardPaths.static.reInvestments,
                search,
              },
              {
                replace: false,
              },
            ),
        },
        {
          label: 'Payments',
          icon: PaymentsGrayIcon,
          activeIcon: PaymentsWhiteIcon,
          disabledIcon: PaymentsGrayIcon,
          pathname: issuerDashboardPaths.static.payments,
          onClick: () =>
            navigate(
              {
                pathname: issuerDashboardPaths.static.payments,
                search,
              },
              {
                replace: false,
              },
            ),
        },
      ],
    },
    {
      label: 'Holders',
      icon: UserManagementIcon,
      activeIcon: UserManagementWhiteIcon,
      disabledIcon: UserManagementInactiveIcon,

      pathname: issuerDashboardPaths.static.holders,
      onClick: () =>
        navigate(
          {
            pathname: issuerDashboardPaths.static.holders,
            search,
          },
          {
            replace: false,
          },
        ),
    },
    {
      label: 'Issuers',
      icon: IssuerGrayIcon,
      activeIcon: IssuerWhiteIcon,
      disabledIcon: IssuerGrayIcon,
      disabled: userRole !== Role.SR_ISSUER_ADMIN,
      disabledHint: 'This feature is only available to issuer administrators.',
      pathname: issuerDashboardPaths.static.issuers,
      onClick: () =>
        navigate(
          {
            pathname: issuerDashboardPaths.static.issuers,
            search,
          },
          {
            replace: false,
          },
        ),
    },
    {
      label: 'Reports',
      icon: FileGreyIcon,
      activeIcon: FileWhiteIcon,
      disabledIcon: FileGreyIcon,
      pathname: issuerDashboardPaths.static.reports,
      onClick: () =>
        navigate(
          {
            pathname: issuerDashboardPaths.static.reports,
            search,
          },
          {
            replace: false,
          },
        ),
    },
    {
      label: 'Documents',
      icon: FilesIcon,
      activeIcon: FilesWhiteIcon,
      disabledIcon: FilesIcon,
      pathname: issuerDashboardPaths.static.documents,
      onClick: () =>
        navigate(
          {
            pathname: issuerDashboardPaths.static.documents,
            search,
          },
          {
            replace: false,
          },
        ),
    },
    {
      label: 'Custody',
      icon: SeedingIcon,
      activeIcon: SeedingWhiteIcon,
      disabledIcon: SeedingDisabledIcon,
      pathname: issuerDashboardPaths.static.portfolios,
      onClick: () =>
        navigate(
          {
            pathname: issuerDashboardPaths.static.portfolios,
            search,
          },
          {
            replace: false,
          },
        ),
    },
    {
      label: 'Communication',
      icon: MessageChatIcon,
      activeIcon: WhiteMessageChatIcon,
      disabledIcon: MessageChatDisabledIcon,
      pathname: issuerDashboardPaths.static.communications,
      onClick: () =>
        navigate(
          {
            pathname: issuerDashboardPaths.static.communications,
            search,
          },
          {
            replace: false,
          },
        ),
    },
    {
      label: 'Event logs',
      icon: NotificationsGrayIcon,
      activeIcon: NotificationsWhiteIcon,
      disabledIcon: NotificationsDisabledIcon,
      notification: isAllNotificationRead?.all_is_read === false,
      pathname: issuerDashboardPaths.static.notifications,
      onClick: () =>
        navigate(
          {
            pathname: issuerDashboardPaths.static.notifications,
            search,
          },
          {
            replace: false,
          },
        ),
    },
    {
      label: (
        <span className="flex items-center gap-3">
          AI Chat <BetaBadge />
        </span>
      ),
      icon: AIChatGrayIcon,
      activeIcon: AIChatWhiteIcon,
      disabledIcon: AIChatGrayIcon,
      pathname: issuerDashboardPaths.static.aiChat,
      onClick: () =>
        navigate(
          {
            pathname: issuerDashboardPaths.static.aiChat,
            search,
          },
          {
            replace: false,
          },
        ),
    },
    {
      label: 'Blockchain',
      icon: BlockchainWhiteIcon,
      activeIcon: BlockchainWhiteIcon,
      disabledIcon: BlockchainGrayIcon,
      disabled: true,
      pathname: issuerDashboardPaths.static.blockchain,
      tooltipHelpText: 'We are working on it',
    },
    {
      label: 'Meetings',
      icon: MeetingGrayIcon,
      activeIcon: MeetingWhiteIcon,
      disabledIcon: MeetingGrayIcon,
      disabled: true,
      pathname: issuerDashboardPaths.static.meetings,
      tooltipHelpText: 'We are working on it',
    },
  ];
};
